import { h } from 'preact';
import { Section } from '../section';
import { Question } from '../question';
import { RECORDING_STATUSES } from '../reporting';
import { NewselaReviewScreen } from '../post_submit';
import {
    COMPOSITION_TYPES,
    ANSWER_STATUS
} from "constants/annotations";

export class QuizScene {
    constructor({components,index,last = false}) {
        this.last = last;
        this.index = index;
        this.components = [...components];
    }

    // provides QuizAnnotation compatible "state" for a given scene
    get attributes() {
        return {
            start: false,
            postSubmit: false,
            lastQuestion: false,
            lastSection: false,
            requiredQuestion: false,
            showSectionDetails: false
        };
    }

    get contributesProgress() {
        return false;
    }
    get shouldSkip() {
        return false;
    }
}

export class SectionQuizScene extends QuizScene {
    constructor(...args) {
        super(...args);

        const [section] = this.components;
        const questions = section?.descendants?.filter(d => d.type === 'question') || [];
        this.numQuestions = questions.length;
    }

    get attributes() {
        const [section] = this.components;
        
        const attributes = {
            ...super.attributes,
            showSectionDetails: true,
            currentQuestion: '',
            currentQuestionNum: '',
            currentSection: section.id,
            currentSectionNum: this.index,
            lastSection: this.last
        };
        
        if (this.numQuestions === 0) {
            // for empty sections, section display is the de facto "last question"
            attributes.lastQuestion = true
        }

        if (this.index === 0) {
            attributes.start = true;
        }

        return attributes;
    }

    render({
        currentSection,
        showSectionDetails,
        postSubmit,
        styles = {}
    }) {
        const { QUIZ } = COMPOSITION_TYPES;
        const [props] = this.components;
        const description = props.properties?.description;
        const thumbnail = props.properties?.thumbnail;
        const style = {
            display:
                currentSection === props.id &&
                showSectionDetails &&
                !postSubmit ?
                    'block':
                    'none',
            height: '100%'
        };

        const sectionStyles = {
            title: styles[QUIZ.SECTION_TITLE] || {},
            description: styles[QUIZ.SECTION_DESCRIPTION] || {},
            ...(props.styles || {})
        };

        return (
            <div style={style}>
                <Section {...props} description={description} styles={sectionStyles} thumbnail={thumbnail} />
            </div>
        )
    }

    get shouldSkip() {
        const [props] = this.components;
        const { content, properties = {} } = props;
        const { thumbnail, description } = properties;
        
        return (
            !content &&
            !description &&
            !thumbnail
        )
    }
}

export class QuestionQuizScene extends QuizScene {
    constructor({ parent, defaultValue, ...args }) {
        super({...args});
        this.defaultValue = defaultValue;
        this.parent = parent;
    }

    get attributes() {
        const [question] = this.components;

        const attributes = {
            ...super.attributes,
            ...this.parent.attributes,
            start: false,
            showSectionDetails: false,
            currentQuestion: question.id,
            currentQuestionNum: this.index,
            lastQuestion: this.last,
            requiredQuestion: question.properties?.required
        };

        if (
            this.index === 0 &&
            this.parent.index === 0 &&
            this.parent.shouldSkip
        ) {
            attributes.start = true;
        }

        return attributes;
    }
    
    get contributesProgress() {
        return true;
    }

    get id() {
        const [question] = this.components;
        return question.id;
    }

    get correctAnswerIndex() {
        const [question] = this.components;
        return question.answers.findIndex(a => a.properties?.correct);
    }

    get answers() {
        const [question] = this.components;
        return question.answers;
    }

    render({
        currentQuestion,
        postSubmit,
        showingCorrect,
        styles = {},
        answers,
        recordingResponseStatus,
        answeredCorrectly = [],
        answeredIncorrectly = []
    }) {
        const [props] = this.components;
        const displayed = currentQuestion === props.id && !postSubmit;
        const description = props.properties?.description;
        const thumbnail = props.properties?.thumbnail;
        const { QUIZ } = COMPOSITION_TYPES;
        const questionStyles = {
            title: styles[QUIZ.QUESTION_TITLE] || {},
            description: styles[QUIZ.QUESTION_DESCRIPTION] || {},
            answer: styles[QUIZ.ANSWER] || {},
            ...(props.styles || {})
        };
        const attrs = {
            ...props,
            displayed,
            thumbnail,
            description,
            showingCorrect,
            answeredCorrectly,
            answeredIncorrectly,
            selectedAnswer: answers[this.id],
            disabled: recordingResponseStatus !== RECORDING_STATUSES.NOT_STARTED,
            styles: questionStyles,
            answered: ANSWER_STATUS.NOT_ANSWERED,
            defaultValue: this.defaultValue
        };
        
        if (showingCorrect && answeredCorrectly.includes(props.id)) {
            attrs.answered = ANSWER_STATUS.ANSWERED_CORRECTLY;
        } else if (showingCorrect && answeredIncorrectly.includes(props.id)) {
            attrs.answered = ANSWER_STATUS.ANSWERED_INCORRECTLY;
        }

        return (
            <Question {...attrs} />
        )
    }
}

export class PostSubmitQuizScene extends QuizScene {
    constructor(...args) {
        super(...args);
        const [arg] = args;
        this.questions = arg?.questions || [];
    }

    get attributes() {
        return {
            ...super.attributes,
            preSubmit: false,
            postSubmit: true,
            lastQuestion: true,
            lastSection: true
        }
    }

    render({
        styles,
        postSubmit,
        answers,
        ...args
    }) {
        const { QUIZ } = COMPOSITION_TYPES;
        return (
            <NewselaReviewScreen
                {...args}
                answers={answers}
                questions={this.questions}
                displayed={postSubmit}
                messages={this.components}
                styles={styles[QUIZ.MESSAGE_FINAL]}
            />
        )
    }
}